import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, Check } from 'lucide-react';

// A dropdown filter control. Supports multi-select (checkbox list — pick as many
// as you like) and single-select (used for "Group by"). Closes on outside click.
// Default state = nothing selected = "show all" (the trigger reads the plain label).

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  border-radius: var(--radius-sm);
  background: ${props => (props.$active ? 'rgba(28, 117, 188, 0.1)' : 'var(--bg-card)')};
  border: 1px solid ${props => (props.$active ? 'var(--color-accent)' : 'var(--color-border)')};
  color: ${props => (props.$active ? 'var(--color-accent)' : 'var(--color-text)')};
  font-family: var(--font-system);
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover { border-color: var(--color-accent); }

  .count-badge {
    background: var(--color-accent);
    color: #fff;
    border-radius: 99px;
    font-size: 0.6875rem;
    padding: 1px 6px;
    line-height: 1.4;
  }

  svg { color: var(--color-text-muted); }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 50;
  min-width: 260px;
  max-width: 380px;
  max-height: 340px;
  overflow-y: auto;
  background: var(--bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 4px;
`;

const ClearRow = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-accent);
  font-family: var(--font-system);
  font-size: 0.75rem;
  padding: 6px 10px;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const OptRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  padding-left: ${props => (props.$indent ? '26px' : '8px')};
  background: ${props => (props.$selected ? 'rgba(28, 117, 188, 0.08)' : 'transparent')};
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-align: left;
  font-family: var(--font-system);
  font-size: 0.875rem;
  color: var(--color-text);

  &:hover { background: rgba(0, 0, 0, 0.04); }

  .opt-label { flex: 1 1 auto; }
  .opt-count { color: var(--color-text-muted); font-size: 0.75rem; flex: 0 0 auto; }
`;

const Box = styled.span`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: ${props => (props.$multi ? '4px' : '99px')};
  border: 1.5px solid ${props => (props.$selected ? 'var(--color-accent)' : 'var(--color-border)')};
  background: ${props => (props.$selected ? 'var(--color-accent)' : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

function FilterDropdown({
  label,
  options,          // [{ value, label, count?, indent? }]
  selectedValues,   // array of selected values
  onToggle,         // (value) => void
  onClear,          // () => void  (multi only)
  multi = true,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const count = selectedValues.length;
  const selectedLabel =
    !multi && count > 0
      ? options.find(o => o.value === selectedValues[0])?.label
      : null;

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        $active={count > 0}
        onClick={() => setOpen(o => !o)}
      >
        {label}
        {multi && count > 0 && <span className="count-badge">{count}</span>}
        {!multi && selectedLabel && `: ${selectedLabel}`}
        <ChevronDown size={14} />
      </Trigger>
      {open && (
        <Panel>
          {multi && count > 0 && onClear && (
            <ClearRow type="button" onClick={onClear}>
              Clear selection
            </ClearRow>
          )}
          {options.map(o => {
            const selected = selectedValues.includes(o.value);
            return (
              <OptRow
                key={o.value}
                type="button"
                $selected={selected}
                $indent={o.indent}
                onClick={() => {
                  onToggle(o.value);
                  if (!multi) setOpen(false);
                }}
              >
                <Box $selected={selected} $multi={multi}>
                  {selected && <Check size={11} strokeWidth={3} />}
                </Box>
                <span className="opt-label">{o.label}</span>
                {o.count != null && <span className="opt-count">{o.count}</span>}
              </OptRow>
            );
          })}
        </Panel>
      )}
    </Wrap>
  );
}

export default FilterDropdown;
